import { useState } from 'react';
import { Button, Flex, useToast } from '@chakra-ui/react';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { PageContent } from '@/components/PageContent';
import { SectionBox } from '@/components/SectionBox';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import {
  usePublishFundraisingCampaignMutation,
  useEndFundraisingCampaignMutation,
  useDeleteFundraisingCampaignMutation,
} from '@/services/queries/fundraising-campaign';
import { breadcrumb } from './breadcrumb';
import { FundraisingCampaignFiltersBox } from './components/FundraisingCampaignFiltersBox';
import { FundraisingCampaignTable } from './components/FundraisingCampaignTable';
import { Filters } from './types';
import { FundraisingCampaignJustifyExpensesModal } from './components/FundraisingCampaignJustifyExpensesModal';
import { DateFilterInputValue } from '@/components/DateRangeFilterFields/types';
import { useFundraisingCampaign } from '@/services/queries/fundraising-campaign';
import { FundraisingCampaign } from '@/model';
import { FundraisingCampaignCreateModal } from './components/FundraisingCampaignCreateModal';
import { FundraisingCampaignEditModal } from './components/FundraisingCampaignEditModal';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { queryClient } from '@/services/api/queryClient';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useTranslation } from 'react-i18next';

const sortOrder = 'desc';
const sortField = 'created_at';

export const FundraisingCampaignScreen = () => {
  const { t } = useTranslation();
  const [selectedFundraisingCampaign, setSelectedFundraisingCampaign] = useState<FundraisingCampaign | undefined>(undefined);
  const [justifyExpensesModalOpen, setJustifyExpensesModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [endModalOpen, setEndModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });
  const toast = useToast();
  const publishMutation = usePublishFundraisingCampaignMutation();
  const endMutation = useEndFundraisingCampaignMutation();
  const deleteMutation = useDeleteFundraisingCampaignMutation();
  const filtersForm = useFiltersForm<Filters>({
    defaultValues: {
      name: '',
      status: '',
      dateValue: DateFilterInputValue.ALL,
      end_date: '',
      start_date: '',
    },
  });
  const filterData = filtersForm.watch();

  const params = GetParamsObject(filterData, pagination.pageNumber, pagination.pageSize, sortOrder, sortField);

  const { data: itemData, isLoading } = useFundraisingCampaign(params);

  const itemsData = itemData?.data ?? [];

  const handlePublish = () => {
    if (!selectedFundraisingCampaign) return;

    publishMutation.mutate(selectedFundraisingCampaign.id, {
      onSuccess: () => {
        toast({
          title: t('pages.fundraising_campaign.fundraising_campaign_screen_publish_success_toast'),
          status: 'success',
        });
        setPublishModalOpen(false);
        setSelectedFundraisingCampaign(undefined);
        queryClient.invalidateQueries(['GET_FUNDRAISING_CAMPAIGN', params]);
      },
      onError: () => {
        toast({
          title: t('pages.fundraising_campaign.fundraising_campaign_screen_publish_error_toast'),
          status: 'error',
        });
      },
    });
  };

  const handleEnd = () => {
    if (!selectedFundraisingCampaign) return;

    endMutation.mutate(selectedFundraisingCampaign.id, {
      onSuccess: () => {
        toast({
          title: t('pages.fundraising_campaign.fundraising_campaign_screen_end_success_toast'),
          status: 'success',
        });
        setEndModalOpen(false);
        setSelectedFundraisingCampaign(undefined);
        queryClient.invalidateQueries(['GET_FUNDRAISING_CAMPAIGN', params]);
      },
      onError: () => {
        toast({
          title: t('pages.fundraising_campaign.fundraising_campaign_screen_end_error_toast'),
          status: 'error',
        });
      },
    });
  };

  const handleDelete = () => {
    if (!selectedFundraisingCampaign) return;

    deleteMutation.mutate(selectedFundraisingCampaign.id, {
      onSuccess: () => {
        toast({
          title: t('pages.fundraising_campaign.fundraising_campaign_screen_delete_success_toast'),
          status: 'success',
        });
        setDeleteModalOpen(false);
        setSelectedFundraisingCampaign(undefined);
        queryClient.invalidateQueries(['GET_FUNDRAISING_CAMPAIGN', params]);
      },
      onError: () => {
        toast({
          title: t('pages.fundraising_campaign.fundraising_campaign_screen_delete_error_toast'),
          status: 'error',
        });
      },
    });
  };

  return (
    <PageContent
      title={t('pages.fundraising_campaign.fundraising_campaign_screen_page_title')}
      breadcrumbItems={breadcrumb}
      actionsContent={
        <Button
          display='flex'
          alignItems='center'
          gap={2}
          onClick={() => setCreateModalOpen(true)}
          width={{ base: '100%', md: 'initial' }}
        >
          <ChakraFeatherIcon name='plus-circle' />
          {t('pages.fundraising_campaign.fundraising_campaign_screen_create_button')}
        </Button>
      }
    >
      <Flex direction='column' gap='small'>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <FundraisingCampaignFiltersBox filtersForm={filtersForm} />
        </SectionBox>

        <FundraisingCampaignTable
          isLoading={isLoading}
          data={itemsData}
          sorting={sorting}
          pageNumber={pagination.pageNumber}
          pageSize={pagination.pageSize}
          totalPages={itemData?.meta.pages ?? 0}
          totalItems={itemData?.meta.total ?? 0}
          onSortingChange={(field, desc) => setSorting({ field, desc })}
          onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
          onEditClick={item => {
            setSelectedFundraisingCampaign(item);
            setEditModalOpen(true);
          }}
          onDeleteClick={selectedItem => {
            setSelectedFundraisingCampaign(selectedItem);
            setDeleteModalOpen(true);
          }}
          onEndClick={selectedItem => {
            setSelectedFundraisingCampaign(selectedItem);
            setEndModalOpen(true);
          }}
          onJustifyExpensesClick={selectedItem => {
            setSelectedFundraisingCampaign(selectedItem);
            setJustifyExpensesModalOpen(true);
          }}
          onPublishClick={selectedItem => {
            setSelectedFundraisingCampaign(selectedItem);
            setPublishModalOpen(true);
          }}
        />
      </Flex>

      <FundraisingCampaignJustifyExpensesModal
        campaignId={selectedFundraisingCampaign?.id as string}
        isOpen={justifyExpensesModalOpen}
        onClose={() => {
          setJustifyExpensesModalOpen(false);
          setSelectedFundraisingCampaign(undefined);
        }}
        onDone={() => {
          setJustifyExpensesModalOpen(false);
          setSelectedFundraisingCampaign(undefined);
        }}
      />

      <FundraisingCampaignCreateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onDone={() => {
          setCreateModalOpen(false);
          queryClient.invalidateQueries(['GET_FUNDRAISING_CAMPAIGN', params]);
        }}
      />

      {selectedFundraisingCampaign ? (
        <FundraisingCampaignEditModal
          item={selectedFundraisingCampaign as FundraisingCampaign}
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedFundraisingCampaign(undefined);
          }}
          onDone={() => {
            setEditModalOpen(false);
            setSelectedFundraisingCampaign(undefined);
            queryClient.invalidateQueries(['GET_FUNDRAISING_CAMPAIGN', params]);
          }}
        />
      ) : null}

      <ConfirmationModal
        isOpen={publishModalOpen}
        onClose={() => {
          setPublishModalOpen(false);
          setSelectedFundraisingCampaign(undefined);
        }}
        onConfirm={handlePublish}
        title={t('pages.fundraising_campaign.fundraising_campaign_screen_publish_modal_title')}
        description={t('pages.fundraising_campaign.fundraising_campaign_screen_publish_modal_description')}
      />

      <ConfirmationModal
        isOpen={endModalOpen}
        onClose={() => {
          setEndModalOpen(false);
          setSelectedFundraisingCampaign(undefined);
        }}
        onConfirm={handleEnd}
        title={t('pages.fundraising_campaign.fundraising_campaign_screen_end_modal_title')}
        description={t('pages.fundraising_campaign.fundraising_campaign_screen_end_modal_description')}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedFundraisingCampaign(undefined);
        }}
        onConfirm={handleDelete}
        title={t('pages.fundraising_campaign.fundraising_campaign_screen_delete_modal_title')}
        description={t('pages.fundraising_campaign.fundraising_campaign_screen_delete_modal_description')}
        confirmLabel={t('pages.fundraising_campaign.fundraising_campaign_screen_delete_modal_confirm_label')}
        confirmButtonProps={{
          background: 'red.500',
          _hover: {
            background: 'red.600',
          },
        }}
      />

      <CenteredSpinner isLoading={publishMutation.isLoading || endMutation.isLoading || deleteMutation.isLoading} />
    </PageContent>
  );
};
